import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { Organization } from "../../../../core/models/entities/Organization";
import { DesignCreateForm_VM } from "../components/design-create-form.component";
import { useTranslation } from "react-i18next";
import { useErrorMessage } from "../../../hooks/useErrorMessage";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useDesignActionHandler } from "../../design-list/hooks/useDesignActionHandler";
import { useState } from "react";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { CreateDesignForm } from "../models/CreateDesignForm";
import { Style } from "../../../../core/models/entities/Style";
import { toast } from "react-toastify";
import { isEmpty } from "../../../../utils/functions/StringUtil";
import { Role } from "../../../../core/models/entities/Role";
import { isAllowed } from "../../../shared/functions/isAllowed";
import { Design } from "../../../../core/models/entities/Design";
import { useSearchParams } from "react-router-dom";
import { useDialog } from "../../../components/Dialogs/dialog-base";
import { CustomStyleUploadData } from "../../../../data/repositories/StyleRepository";

export function useDesignCreateFormViewModel(
  manufacturers: Manufacturer[],
  customers: Organization[]
): DesignCreateForm_VM {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const logoId = searchParams.get("addLogoId");
  const initialTitle = searchParams.get("logoTitle") ?? "";
  const { tError } = useErrorMessage();
  const { role, organizationId } = useProfileContext();
  const { styleRepository } = useRepositoriesContext();
  const {
    handleCreateDesign,
    isLoading,
    handleGoToUpdateDesign,
    handleGoToDesignList,
  } = useDesignActionHandler();
  const [pageQuery, setPageQuery] = useState<PageQuery>({
    page: { index: 1, size: 50 },
  } as PageQuery);
  const [form, setForm] = useState<CreateDesignForm>({
    organization: role === Role.EndUser ? customers[0] : undefined,
    title: initialTitle,
  } as CreateDesignForm);
  const [vendor, setVendor] = useState<Organization | null>(null);
  const customStyleDialog = useDialog();
  const [isCreatingCustomStyle, setIsCreatingCustomStyle] = useState(false);

  const {
    data,
    fetchNextPage,
    isLoading: isStylesLoading,
  } = styleRepository.useAllStyleAsInfinityQuery(pageQuery);
  let styles: Style[] = [];
  if (data) {
    if (!form.style) {
      setForm((f) => ({ ...f, style: data.pages[0].results[0] }));
    }
    for (const datum of data.pages) {
      styles = styles.concat(datum.results);
    }

    // Sort styles to prioritize exact matches when there's a search term
    if (pageQuery.searchTerm && pageQuery.searchTerm.trim() !== "") {
      const searchTerm = pageQuery.searchTerm.toLowerCase().trim();
      styles = styles.sort((a, b) => {
        // Calculate match scores for style a
        const aExactModel = a.model.toLowerCase() === searchTerm ? 100 : 0;
        const aExactName = a.name.toLowerCase() === searchTerm ? 100 : 0;
        const aExactModelAndName =
          a.modelAndName.toLowerCase() === searchTerm ? 100 : 0;
        const aStartsWithModel = a.model.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const aStartsWithName = a.name.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const aStartsWithModelAndName = a.modelAndName
          .toLowerCase()
          .startsWith(searchTerm)
          ? 50
          : 0;
        const aContainsModel = a.model.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const aContainsName = a.name.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const aContainsModelAndName = a.modelAndName
          .toLowerCase()
          .includes(searchTerm)
          ? 10
          : 0;

        const aScore = Math.max(
          aExactModel,
          aExactName,
          aExactModelAndName,
          aStartsWithModel,
          aStartsWithName,
          aStartsWithModelAndName,
          aContainsModel,
          aContainsName,
          aContainsModelAndName
        );

        // Calculate match scores for style b
        const bExactModel = b.model.toLowerCase() === searchTerm ? 100 : 0;
        const bExactName = b.name.toLowerCase() === searchTerm ? 100 : 0;
        const bExactModelAndName =
          b.modelAndName.toLowerCase() === searchTerm ? 100 : 0;
        const bStartsWithModel = b.model.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const bStartsWithName = b.name.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const bStartsWithModelAndName = b.modelAndName
          .toLowerCase()
          .startsWith(searchTerm)
          ? 50
          : 0;
        const bContainsModel = b.model.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const bContainsName = b.name.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const bContainsModelAndName = b.modelAndName
          .toLowerCase()
          .includes(searchTerm)
          ? 10
          : 0;

        const bScore = Math.max(
          bExactModel,
          bExactName,
          bExactModelAndName,
          bStartsWithModel,
          bStartsWithName,
          bStartsWithModelAndName,
          bContainsModel,
          bContainsName,
          bContainsModelAndName
        );

        // Sort by score (higher scores first), then by model name alphabetically
        if (bScore !== aScore) {
          return bScore - aScore;
        }
        return a.modelAndName.localeCompare(b.modelAndName);
      });
    }
  }

  function handleNexPage() {
    fetchNextPage().then();
  }

  function handleFilterOnManufacturer(manufacturer: Manufacturer | null) {
    setPageQuery((x) => ({
      ...x,
      filterBy: manufacturer
        ? [
            {
              field: "manufacturer.id",
              value: "" + manufacturer.id,
            },
          ]
        : [],
    }));
  }

  function handleSearchQueryChange(query: string) {
    setPageQuery((x) => ({
      ...x,
      searchTerm: query,
    }));
  }

  function handleSelectCustomer(customer: Organization) {
    setForm((f) => ({ ...f, organization: customer }));
  }

  function handleSelectStyle(style: Style) {
    setForm((f) => ({ ...f, style: style }));
  }

  function handleChangeTitle(title: string) {
    setForm((x) => ({ ...x, title: title }));
  }

  function handleSubmitForm() {
    if (!form) {
      return;
    }

    if (!form.style) {
      toast.warn(t("toasts.no_styles_selected"));
      return;
    }

    const customerId =
      role === Role.Admin && vendor
        ? vendor.id
        : form?.organization?.id ?? null;

    const orgId = role === Role.Admin && vendor ? vendor.id : organizationId;

    const title = isEmpty(form.title) ? "New Design" : form.title;

    handleCreateDesign({
      title,
      customerId: customerId,
      orgId: orgId,
      styleId: form.style.id,
    })
      .then((design) => {
        handleGoToUpdateDesign(
          design as Design,
          logoId ? Number(logoId) : undefined
        );
      })
      .catch((e) => tError(e, "Failed to create"));
  }

  async function handleCustomStyleSubmit(data: CustomStyleUploadData) {
    if (!organizationId) {
      toast.error("Organization not found");
      return;
    }
    setIsCreatingCustomStyle(true);
    try {
      const style = await styleRepository.createUserUploadedStyle(
        organizationId,
        data
      );
      // Set the newly created style as the selected style
      setForm((f) => ({
        ...f,
        style,
        title: f.title || data.modelName,
      }));
      customStyleDialog.closeDialog();
      toast.success("Custom style created successfully");
    } catch (e) {
      tError(e as Error, "Failed to create custom style");
    } finally {
      setIsCreatingCustomStyle(false);
    }
  }

  function handleCancel() {
    handleGoToDesignList();
  }

  return {
    translate: t,
    styles,
    isStylesLoading,
    onLoadMoreStyles: handleNexPage,
    manufacturers: manufacturers,
    onFilterByManufacturer: handleFilterOnManufacturer,
    searchQuery: pageQuery.searchTerm ?? "",
    onSearchQueryChange: handleSearchQueryChange,
    selectedStyle: form.style,
    onSelectStyle: handleSelectStyle,
    showCustomerInfo: isAllowed(role!, Role.Vendor),
    customers,
    title: form.title,
    selectedCustomer: form.organization!,
    onChangeTitle: handleChangeTitle,
    onSelectCustomer: handleSelectCustomer,
    selectedVendor: vendor,
    onSelectVendor: (selectedVendor: Organization | null) => {
      setVendor(selectedVendor);
    },
    onSubmit: handleSubmitForm,
    disableSubmit: !validateForm(form),
    onCancel: handleCancel,
    isLoading: isLoading,
    role: role!,
    customStyleDialog,
    onCustomStyleSubmit: handleCustomStyleSubmit,
    isCreatingCustomStyle,
  };
}

function validateForm(form: CreateDesignForm): boolean {
  return !!form.style;
}
